import { html } from "lit";
import { customElement, query, state } from "lit/decorators.js";

import DeviceController from "@/controllers/device-controller";

import { parseSecondsToMilliseconds } from "@/internals/time-utils";
import { filterElementsByTagNames } from "@/internals/elements";

import type AtlasToast from "@/components/display/atlas-toast/atlas-toast";
import AtlasElement, { type AtlasElementProps } from "@/components/atlas-element";

import styles from "./atlas-toast-container.scss";

export type ToastContainerProps = AtlasElementProps;

/**
 * @dependency atlas-toast
 * 
 * @slot - O slot padrão é onde os toasts serão inseridos
 * 
 * @tag atlas-toast-container
 */
@customElement("atlas-toast-container")
export default class AtlasToastContainer extends AtlasElement {
    static styles = styles;

    @state() private _toasts: AtlasToast[] = [];

    @state() private _toastCount: number = 0;

    @state() private _isExpanded: boolean = false;

    @query(".atlas-toast-container")
    private _hoverArea: HTMLElement;

    private MAX_TOASTS_ON_SCREEN: number = 3;

    private BASE_TOAST_Z_INDEX: number = 10000;

    private MIN_ANIMATION_TIMEOUT: number = 10;

    private MOVE_IN_OUT_DURATION: number = 0.6;

    private MOVE_IN_OUT_TRANSITION = `transform ${this.MOVE_IN_OUT_DURATION}s cubic-bezier(0, 0, 0.25, 1)`;

    private EXPAND_COLLAPSE_DURATION: number = 0.8;

    private EXPAND_COLLAPSE_TRANSITION = `transform ${this.EXPAND_COLLAPSE_DURATION}s cubic-bezier(0, 0, 0.25, 1)`;

    private _deviceController: DeviceController = new DeviceController(this);

    constructor() {
        super();

        this.onCloseToast = this.onCloseToast.bind(this);
        this.expandToastContainer = this.expandToastContainer.bind(this);
        this.collapseToastContainer = this.collapseToastContainer.bind(this);
    }

    /** @internal */
    public connectedCallback(): void {
        super.connectedCallback();
        this.addEventListener("atlas-toast-close", this.onCloseToast);
    }

    /** @internal */
    public disconnectedCallback(): void {
        super.disconnectedCallback();
        this.removeEventListener("atlas-toast-close", this.onCloseToast);
    }

    /**
     * Adiciona um toast ao container.
     * @param toast {AtlasToast} - O toast que será adicionado ao container
     */
    public addToast(toast: AtlasToast) {
        if (toast.tagName.toLowerCase() !== "atlas-toast") return;

        toast.style.transform = "translateX(100%)";

        this.prepend(toast);

        const duration = parseInt(toast.getAttribute("data-duration") || "0", 10);

        if (duration <= 0) return;

        setTimeout(() => {
            this.removeToast(toast);
        }, duration);
    }

    /**
     * Remove o toast do container.
     * @param toast {AtlasToast} - O toast que será removido
     */
    public removeToast(toast: AtlasToast) {
        const toastIndex = this._toasts.indexOf(toast);

        if (toastIndex === -1) return;

        const removeToast = this._toasts[toastIndex];

        setTimeout(() => this.moveInOutAnimation(removeToast, false), this.MIN_ANIMATION_TIMEOUT);

        setTimeout(() => {
            removeToast.remove();
        }, this.getMoveInOutAnimationTimeout());
    }

    private onCloseToast(event: CustomEvent) {
        const toast = event.target as AtlasToast;
        this.removeToast(toast);
    }

    private onSlotChange() {
        const slot = this.shadowRoot?.querySelector("slot:not([name])") as HTMLSlotElement;

        const slottedElements= slot?.assignedElements({ flatten: true }) || [];

        this._toasts = filterElementsByTagNames(slottedElements, ["ATLAS-TOAST"]);

        if (this._toasts.length === 0) return;

        const isAddingToast = this._toasts.length >= this._toastCount;

        const shouldRemoveLastToast = (this._toastCount >= this.MAX_TOASTS_ON_SCREEN) && isAddingToast;

        this.updateToastsOnScreen(isAddingToast, shouldRemoveLastToast);

        this._toastCount = this._toasts.length;
    }

    private getMoveInOutAnimationTimeout() {
        const animationAdjustment = 100;

        return parseSecondsToMilliseconds(this.MOVE_IN_OUT_DURATION) - animationAdjustment;
    }

    private getExpandCollapseAnimationTimeout() {
        return parseSecondsToMilliseconds(this.EXPAND_COLLAPSE_DURATION);
    }

    private moveInOutAnimation(toast: AtlasToast, isAddingToast: boolean) {
        let baseTranslation = "0%";

        if (this._deviceController.isMobile) {
            const hoverAreaWidth = this._hoverArea.getBoundingClientRect().width;
            const toastWidth = toast.getBoundingClientRect().width;
            baseTranslation = `${(hoverAreaWidth - toastWidth) / 2}px`;
        }

        const translationX = isAddingToast ? baseTranslation : "100%";

        toast.style.transition = this.MOVE_IN_OUT_TRANSITION;
        toast.style.transform = this.combineTransformStyle(toast, `translateX(${translationX})`);
    }

    private combineTransformStyle(toast: AtlasToast, includingTransform: string): string {
        const currentTransform = toast.style.transform;

        const regexToMatchOnlySpaceSeparatedTransforms = /\s+(?![^()]*\))/g;

        const currentTransformParts = currentTransform.split(regexToMatchOnlySpaceSeparatedTransforms);

        const newTransform = currentTransformParts.filter((transform) => {
            if (transform.includes("translateX") && includingTransform.includes("translateX")) return false;
            if (transform.includes("scale3d") && includingTransform.includes("scale3d")) return false;
            if (transform.includes("translateY") && includingTransform.includes("translateY")) return false;

            return true;
        });

        newTransform.push(includingTransform);
        const newTransformString = newTransform.join(" ").trim();

        return newTransformString;
    }

    private updateToastQueueOrder() {
        this._toasts.forEach((toast, index) => {
            toast.style.transition = this.EXPAND_COLLAPSE_TRANSITION;

            this.updateZIndex(toast, index);
            this.updateScale(toast, index);

            setTimeout(() => {
                this.updateTranslation(toast, index);
            }, this.MIN_ANIMATION_TIMEOUT);
        });
    }

    private updateZIndex(toast: AtlasToast, index: number) {
        toast.style.zIndex = `${this.BASE_TOAST_Z_INDEX - 1 - index}`;
    }

    private updateScale(toast: AtlasToast, index: number) {
        const baseScale = 1;
        const shrinkingFactor = 0.05;
        const scale = this._isExpanded ? baseScale : baseScale - shrinkingFactor * index;
        toast.style.transform = this.combineTransformStyle(toast, `scale3d(${scale}, ${scale}, 1)`);
    }

    private updateTranslation(toast: AtlasToast, index: number) {
        const baseTranslation = this._isExpanded ? 8 : 16;

        if (index === 0) {
            toast.style.transform = this.combineTransformStyle(toast, "translateY(0)");
            toast.setAttribute("data-translation-y", "0");
            return;
        }

        const toastAboveElement = this._toasts[index - 1];

        const toastAbove = {
            element: toastAboveElement,
            height: toastAboveElement.getBoundingClientRect().height,
            translationY: parseFloat(toastAboveElement.getAttribute("data-translation-y") || "0")
        };

        let translateY = 0;

        const totalNeededHeightToTranslateY = toastAbove.height + toastAbove.translationY;

        const currentToastHeight = toast.getBoundingClientRect().height;

        if (this._isExpanded) {
            translateY = totalNeededHeightToTranslateY + baseTranslation;
        } else if (currentToastHeight <= totalNeededHeightToTranslateY) {
            translateY = totalNeededHeightToTranslateY + baseTranslation - currentToastHeight;
        } else {
            translateY = 0;
        }

        toast.style.transform = this.combineTransformStyle(toast, `translateY(${translateY}px)`);
        toast.setAttribute("data-translation-y", translateY.toString());
    }

    private updateToastsOnScreen(isAddingToast: boolean, shouldRemoveLastToast: boolean) {
        if (isAddingToast) {
            this.updateAddedToast();
        }

        if (shouldRemoveLastToast) {
            this.removeToast(this._toasts[this._toasts.length - 1]);
        }

        const updateQueueAwaitTimeout = isAddingToast
            ? this.getMoveInOutAnimationTimeout()
            : this.MIN_ANIMATION_TIMEOUT;

        setTimeout(() => {
            this.updateToastQueueOrder();

            setTimeout(() => {
                this.updateHoverArea();
            }, this.getExpandCollapseAnimationTimeout());
        }, updateQueueAwaitTimeout);
    }

    private updateAddedToast() {
        const addedToast = this._toasts[0];

        addedToast.addEventListener("mouseover", this.expandToastContainer);
        addedToast.addEventListener("mouseout", this.collapseToastContainer);

        addedToast.style.zIndex = this.BASE_TOAST_Z_INDEX.toString();
        setTimeout(() => this.moveInOutAnimation(addedToast, true), this.MIN_ANIMATION_TIMEOUT);
    }

    private updateHoverArea() {
        if (this._toasts.length === 0) {
            this._hoverArea.style.height = "0";
            return;
        }

        const lastToast = this._toasts[this._toasts.length - 1];

        const { top, height } = lastToast.getBoundingClientRect();

        const containerMarginTop = this._deviceController.isMobile ? 80 : 90;

        this._hoverArea.style.height = `${top + height - containerMarginTop}px`;
    }

    private expandToastContainer() {
        this._isExpanded = true;
        this.updateToastsOnScreen(false, false);
    }

    private collapseToastContainer(event: MouseEvent) {
        const relatedTarget = event.relatedTarget as HTMLElement;
        const targetsToIgnore = [
            "atlas-toast",
            "atlas-button",
            "atlas-toast-container"
        ];

        if (targetsToIgnore.includes(relatedTarget?.tagName?.toLowerCase())) return;

        this._isExpanded = false;
        this.updateToastsOnScreen(false, false);
    }

    /** @internal */
    public render() {
        return html`
            <div class="atlas-toast-container" @mouseover="${this.expandToastContainer}">
                <slot @slotchange="${this.onSlotChange}"></slot>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "atlas-toast-container": AtlasToastContainer;
    }
}
